import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Users,
  Copy
} from 'lucide-react';
import { facilityAgentAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const TokenHolderRegister = () => {
  const { user, loading: authLoading } = useAuth();
  const userRole = user?.role;
  const isFacilityAgent = userRole === 'facility_agent';
  const isPrimarySubscriber = userRole === 'primary_subscriber';
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterRole, userRole]);

  // Ensure default filter aligns with role permissions
  useEffect(() => {
    if (!userRole) return;
    if (isFacilityAgent) {
      setFilterRole((prev) => (prev === 'primary_subscriber' ? prev : 'primary_subscriber'));
    } else if (isPrimarySubscriber) {
      setFilterRole((prev) => (prev === 'primary_subscriber' ? 'all' : prev));
    }
  }, [isFacilityAgent, isPrimarySubscriber, userRole]);

  const roleFilterOptions = useMemo(() => {
    if (isFacilityAgent) {
      return [{ value: 'primary_subscriber', label: 'Primary Subscriber' }];
    }
    if (isPrimarySubscriber) {
      return [
        { value: 'all', label: 'All Holders' },
        { value: 'custodian', label: 'Custodian' },
        { value: 'investor', label: 'Direct Investor' }
      ];
    }
    return [
      { value: 'all', label: 'All Roles' },
      { value: 'investor', label: 'Investor' },
      { value: 'custodian', label: 'Custodian' }
    ];
  }, [isFacilityAgent, isPrimarySubscriber]);

  // Align selected filter with available options when role changes
  useEffect(() => {
    if (!roleFilterOptions.length) return;
    if (!roleFilterOptions.some(option => option.value === filterRole)) {
      setFilterRole(roleFilterOptions[0].value);
    }
  }, [roleFilterOptions, filterRole]);

  // Fetch users and wallets
  const { data: usersData, isPending: usersLoading } = useQuery({
    queryKey: ['tokenHolders', debouncedSearchTerm, filterRole, currentPage, userRole],
    queryFn: async () => {
      const params = {
        search: debouncedSearchTerm || undefined
      };

      if (filterRole === 'all') {
        if (isFacilityAgent) {
          params.roles = 'primary_subscriber';
        } else if (isPrimarySubscriber) {
          params.roles = 'custodian,investor';
        }
      } else {
        params.role = filterRole;
      }

      // Fetch token holders using facility agent endpoint
      const response = await facilityAgentAPI.getTokenHolders(params);

      const tokenHolders = response?.data?.data?.users || [];

      // Add LEI to each user
      const holdersWithLEI = tokenHolders.map(user => ({
        ...user,
        lei: user.lei || '-'
      }));

      // Sort by creation date (newest first)
      const sortedHolders = holdersWithLEI.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      // Paginate
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedHolders = sortedHolders.slice(startIndex, endIndex);

      return {
        tokenHolders: paginatedHolders,
        pagination: {
          currentPage: currentPage,
          totalPages: Math.ceil(sortedHolders.length / itemsPerPage),
          totalItems: sortedHolders.length,
          itemsPerPage: itemsPerPage
        }
      };
    },
    select: data => data || { tokenHolders: [], pagination: {} },
    enabled: !!userRole && !authLoading
  });
  const tokenHolders = usersData?.tokenHolders || [];
  const pagination = usersData?.pagination || {};
  const headerSubtitle = isFacilityAgent
    ? 'Primary subscribers eligible for allocation'
    : 'Custodians and direct investors with their LEI (Legal Entity Identifier)';
  const isTableLoading = authLoading || usersLoading;

  const getUserDisplayName = (user) => {
    if (user.role === 'investor') {
      if (user.accountType === 'corporate') {
        return user.corporateInfo?.legalEntityName ||
          user.corporateInfo?.companyName ||
          'N/A';
      } else {
        return user.personalInfo?.fullName || 'N/A';
      }
    } else if (user.role === 'custodian') {
      return user.corporateInfo?.legalEntityName ||
        user.corporateInfo?.companyName ||
        'N/A';
    } else if (user.role === 'primary_subscriber') {
      return user.corporateInfo?.legalEntityName ||
        user.corporateInfo?.companyName ||
        'N/A';
    }
    return 'N/A';
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      investor: { label: 'Direct Investor', class: 'bg-blue-100 text-blue-800' },
      custodian: { label: 'Custodian', class: 'bg-purple-100 text-purple-800' },
      primary_subscriber: { label: 'Primary Subscriber', class: 'bg-emerald-100 text-emerald-800' }
    };
    const config = roleConfig[role] || { label: role, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (user) => {
    if (!user.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Inactive
        </span>
      );
    }
    if (user.registrationStatus === 'pending_verification') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    );
  };

  const truncateAddress = (address) => {
    if (!address || address === '-') return address;
    if (address.length <= 20) return address;
    return `${address.slice(0, 10)}...${address.slice(-8)}`;
  };

  const copyToClipboard = async (text, label) => {
    if (!text || text === '-') return;

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bond Holder</h1>
              <p className="mt-2 text-sm text-gray-600">
                {headerSubtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg border border-light-gray p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="sm:w-[70%] w-full">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, user ID, or LEI..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="sm:w-[30%] w-full flex items-center gap-2">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-6 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                disabled={roleFilterOptions.length <= 1}
              >
                {roleFilterOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isTableLoading ? (
            <div className="p-12">
              <LoadingSpinner size="lg" text="Loading token holders..." />
            </div>
          ) : tokenHolders.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No token holders found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No token holders match the current filter criteria.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Bond Holder</h3>
                </div>
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">
                        User ID
                      </th>
                      <th className="table-header-cell">
                        User Name
                      </th>
                      <th className="table-header-cell">
                        Role
                      </th>
                      <th className="table-header-cell">
                        LEI
                      </th>
                      <th className="table-header-cell">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tokenHolders.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-mono text-gray-900">
                              {user._id}
                            </div>
                            <button
                              onClick={() => copyToClipboard(user._id, 'User ID')}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Copy User ID"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {getUserDisplayName(user)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-mono text-gray-900" title={user.lei}>
                              {user.lei}
                            </div>
                            {user.lei !== '-' && (
                              <button
                                onClick={() => copyToClipboard(user.lei, 'LEI')}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Copy LEI"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(user)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={currentPage === pagination.totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === pagination.totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {(currentPage - 1) * itemsPerPage + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * itemsPerPage, pagination.totalItems)}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">{pagination.totalItems}</span>{' '}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeft className="h-5 w-5" />
                        </button>

                        {/* Page numbers */}
                        {[...Array(pagination.totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show first page, last page, current page, and pages around current
                          if (
                            pageNumber === 1 ||
                            pageNumber === pagination.totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setCurrentPage(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}

                        <button
                          onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                          disabled={currentPage === pagination.totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${currentPage === pagination.totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenHolderRegister;
